import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { LocLabelModifier } from 'src/app/shared/components/clickable-row-table/models/data-table-config.models';
import { ExcelFile } from 'src/app/shared/factories/excel-file.factory';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { style, width, percent, fixed } from 'src/app/shared/helpers/table.constants';
import { GenericNoteExportData, genericNoteTableColumns } from 'src/app/shared/models/note-table.model';

export interface ResidentReportData {
  id: string;
  nameSurname: string;
  phone: string;
  email: string;
  address: string;
  birthDate: string;
  PESEL: string;
  documentNo: string;
  nationality: string;
  lastUpdate: string;
  emergencyContact: string;
}

export namespace CommonReport {
  export interface ResidentNote extends GenericNoteExportData {
    name: string | null;
  }

  export const residentsNotesColumns: ExcelFile.Column[] = [
    { header: { key: 'user.nameSurname' }, key: 'name', width: width(165), style },
    ...genericNoteTableColumns
  ];
}

export namespace CommunityReport {
  export interface LandlordData extends TenantData {
    totShares: number;
    totSharesPercent: number;
    apShares: number;
    apSharesPercent: number;
    parShares: number;
    parSharesPercent: number;
    stoShares: number;
    stoSharesPercent: number;
    ownerCode: string;
  }

  export interface TenantData extends ResidentReportData {
    apartmentNumbers: string;
    parkingNumbers: string;
    storageNumbers: string;
    street: string;
    blockNumber: string;
    staircaseNumber: string;
    floor: string;
    number: string;
    landRegister: string;
    areaInSquareMeters: string;
  }

  export interface TenancyData {
    id: string;
    landlordName: string;
    tenantName: string;
    apartmentNumber: string;
    parkingNumbers: string;
    storageNumbers: string;
    tenancyStart: string;
    tenancyEnd: string;
    creatorName: string;
    creationDate: string;
    // cancelDate: string;
  }

  export interface ApartmentData {
    id: string;
    street: string;
    blockNumber: string;
    staircaseNumber: string;
    floor: string;
    number: string;
    rooms: string;
    landRegister: string;
    sharesInEstate: number;
    sharesPercent: number;
    residentsNames: string;
    area: number;
    unitCodes: string;
    parkingNumbers: string;
    storageNumbers: string;
    bankAccountNumber: string;
  }

  export interface ParkingStorageData {
    id: string;
    street: string;
    blockNumber: string;
    staircaseNumber: string;
    floor: string;
    number: string;
    type: string;
    landRegister: string;
    sharesInEstate: number;
    sharesPercent: number;
    residentsNames: string;
    areaInSquareMeters: number;
    unitCodes: string;
    bankAccountNumber: string;
  }
}

const modifier = LocLabelModifier.CAP;

// prettier-ignore
const PERSONAL_DATA_COLUMNS: ExcelFile.Column[] = [
  { header: { key: 'user.nameSurname' }, key: 'nameSurname', width: width(165), style },
  { header: { key: 'user.postAddr'    }, key: 'address',     width: width(185), style },
  { header: { key: 'label.phone'      }, key: 'phone',       width: width(125), style },
  { header: { key: 'label.email'      }, key: 'email',       width: width(255), style }
  // { header: { key: 'user.birthDate'   }, key: 'birthDate',   width: width(125), style },
  // { header: { key: 'user.pesel'       }, key: 'PESEL',       width: width(150), style },
  // { header: { key: 'user.documentNo'  }, key: 'documentNo',  width: width(150), style },
  // { header: { key: 'user.national'    }, key: 'nationality', width: width(185), style },
];

function RENTAL_UNIT_COLUMNS(parkings?: 'parking'): ExcelFile.Column[] {
  const staircase = { header: { key: 'addr.stairNo' }, key: 'staircaseNumber', width: width(55), style };
  // prettier-ignore
  return [
    { header: { key: 'addr.street'       }, key: 'street',      width: width(120), style },
    { header: { key: 'addr.blockNo'      }, key: 'blockNumber', width: width(55),  style },
    ...(!parkings ? [staircase] : []),
    { header: { key: 'addr.floor'        }, key: 'floor',       width: width(65),  style },
    { header: { key: 'addr.no', modifier }, key: 'number',      width: width(60),  style }
  ];
}

export class CommunityReportColumns {
  // prettier-ignore
  public static readonly landlords: ExcelFile.Column[] = [
    { header: { key: 'table.id'                       }, key: 'id',                  width: width(45),   style          },
    ...PERSONAL_DATA_COLUMNS,
    { header: { key: 'label.shares'                   }, key: 'totShares',           width: width(100),  style          },
    { header: { key: 'res.sharePer'                   }, key: 'totSharesPercent',    width: width(100),  style: percent },
    { header: { key: 'commApartment.plural', modifier }, key: 'apartmentNumbers',    width: width(185),  style          },
    ...RENTAL_UNIT_COLUMNS(),
    { header: { key: 'estate.registerNumber'          }, key: 'landRegister',        width: width(200),  style          },
    { header: { key: 'unit.area'                      }, key: 'areaInSquareMeters',  width: width(105),  style: fixed   },
    { header: { key: 'res.apShare'                    }, key: 'apShares',            width: width(100),  style          },
    { header: { key: 'res.apSharePer'                 }, key: 'apSharesPercent',     width: width(100),  style: percent },
    { header: { key: 'parking.plural',       modifier }, key: 'parkingNumbers',      width: width(130),  style          },
    { header: { key: 'res.parShare'                   }, key: 'parShares',           width: width(100),  style          },
    { header: { key: 'res.parSharePer'                }, key: 'parSharesPercent',    width: width(100),  style: percent },
    { header: { key: 'storage.plural',       modifier }, key: 'storageNumbers',      width: width(130),  style          },
    { header: { key: 'res.stoShare'                   }, key: 'stoShares',           width: width(100),  style          },
    { header: { key: 'res.stoSharePer'                }, key: 'stoSharesPercent',    width: width(100),  style: percent },
    { header: { key: 'user.ownerCode'                 }, key: 'ownerCode',           width: width(255),  style          },
    { header: { key: 'user.emergeContact'             }, key: 'emergencyContact',    width: width(255),  style          },
    { header: { key: 'label.lastUpdate'               }, key: 'lastUpdate',          width: width(200),  style          }
  ];

  // prettier-ignore
  public static readonly tenants: ExcelFile.Column[] = [
    { header: { key: 'table.id'                       }, key: 'id',                 width: width(45),   style        },
    ...PERSONAL_DATA_COLUMNS,
    { header: { key: 'commApartment.plural', modifier }, key: 'apartmentNumbers',   width: width(185),  style        },
    ...RENTAL_UNIT_COLUMNS(),
    { header: { key: 'estate.registerNumber'          }, key: 'landRegister',       width: width(200),  style        },
    { header: { key: 'unit.area'                      }, key: 'areaInSquareMeters', width: width(105),  style: fixed },
    { header: { key: 'parking.plural',       modifier }, key: 'parkingNumbers',     width: width(185),  style        },
    { header: { key: 'storage.plural',       modifier }, key: 'storageNumbers',     width: width(185),  style        },
    { header: { key: 'user.emergeContact'             }, key: 'emergencyContact',   width: width(255),  style        },
    { header: { key: 'label.lastUpdate'               }, key: 'lastUpdate',         width: width(200),  style        }
  ];

  // prettier-ignore
  public static tenancies: ExcelFile.Column[] = [
    { header: { key: 'table.id'                           }, key: 'id',               width: width(45),   style },
    { header: { key: 'user.landNameSur'                   }, key: 'landlordName',     width: width(185),  style },
    { header: { key: 'user.tenNameSur'                    }, key: 'tenantName',       width: width(185),  style },
    { header: { key: 'commApartment.nominative', modifier }, key: 'apartmentNumber',  width: width(185),  style },
    { header: { key: 'parking.plural',           modifier }, key: 'parkingNumbers',   width: width(185),  style },
    { header: { key: 'storage.plural',           modifier }, key: 'storageNumbers',   width: width(185),  style },
    { header: { key: 'tenant.accStart'                    }, key: 'tenancyStart',     width: width(185),  style },
    { header: { key: 'tenant.accEnd'                      }, key: 'tenancyEnd',       width: width(185),  style },
    { header: { key: 'user.accCreator'                    }, key: 'creatorName',      width: width(185),  style },
    { header: { key: 'user.accCreateDate'                 }, key: 'creationDate',     width: width(185),  style }
  ];

  // prettier-ignore
  public static apartments(): ExcelFile.Column[] {
    const replaceMap = GrammarHelper.unitCodeTransObj(RentalUnitType.APARTMENT, EstateType.COMMUNITY).replaceMap;
    return [
      /* eslint-disable max-len */
      { header: { key: 'table.id'                                    }, key: 'id',                  width: width(45),   style           },
      ...RENTAL_UNIT_COLUMNS(),
      { header: { key: 'unit.rooms'                                  }, key: 'rooms',               width: width(55),   style           },
      { header: { key: 'estate.registerNumber'                       }, key: 'landRegister',        width: width(200),  style           },
      { header: { key: 'unit.shares'                                 }, key: 'sharesInEstate',      width: width(85),   style           },
      { header: { key: 'label.shareEstate'                           }, key: 'sharesPercent',       width: width(85),   style: percent  },
      { header: { key: 'commLandlords.nominative',          modifier }, key: 'residentsNames',      width: width(200),  style           },
      { header: { key: 'unit.area'                                   }, key: 'area',                width: width(105),  style: fixed    },
      { header: { key: 'unit.code',             replaceMap, modifier }, key: 'unitCodes',           width: width(155),  style           },
      { header: { key: 'parking.plural',                    modifier }, key: 'parkingNumbers',      width: width(130),  style           },
      { header: { key: 'storage.plural',                    modifier }, key: 'storageNumbers',      width: width(130),  style           },
      { header: { key: 'label.bankAccount'                           }, key: 'bankAccountNumber',   width: width(300),  style           }
      /* eslint-enable max-len */
    ];
  }

  // prettier-ignore
  public static parkings(): ExcelFile.Column[] {
    const replaceMap = GrammarHelper.unitCodeTransObj(RentalUnitType.PARKING, EstateType.COMMUNITY).replaceMap;
    return [
      { header: { key: 'table.id'                            }, key: 'id',                width: width(45),   style          },
      ...RENTAL_UNIT_COLUMNS('parking'),
      { header: { key: 'unit.parType'                        }, key: 'type',              width: width(60),   style          },
      { header: { key: 'estate.registerNumber'               }, key: 'landRegister',      width: width(200),  style          },
      { header: { key: 'unit.shares'                         }, key: 'sharesInEstate',    width: width(85),   style          },
      { header: { key: 'label.shareEstate'                   }, key: 'sharesPercent',     width: width(85),   style: percent },
      { header: { key: 'commLandlords.nominative',  modifier }, key: 'residentsNames',    width: width(200),  style          },
      { header: { key: 'unit.code',     replaceMap, modifier }, key: 'unitCodes',         width: width(155),  style          },
      { header: { key: 'label.bankAccount'                   }, key: 'bankAccountNumber', width: width(300),  style          }
    ];
  }

  // prettier-ignore
  public static storages(): ExcelFile.Column[] {
    const replaceMap = GrammarHelper.unitCodeTransObj(RentalUnitType.STORAGE, EstateType.COMMUNITY).replaceMap;
    return [
      { header: { key: 'table.id'                            }, key: 'id',                  width: width(45),   style          },
      ...RENTAL_UNIT_COLUMNS(),
      { header: { key: 'unit.stoType'                        }, key: 'type',                width: width(60),   style          },
      { header: { key: 'estate.registerNumber'               }, key: 'landRegister',        width: width(200),  style          },
      { header: { key: 'unit.shares'                         }, key: 'sharesInEstate',      width: width(85),   style          },
      { header: { key: 'label.shareEstate'                   }, key: 'sharesPercent',       width: width(85),   style: percent },
      { header: { key: 'commLandlords.nominative',  modifier }, key: 'residentsNames',      width: width(200),  style          },
      { header: { key: 'unit.area'                           }, key: 'areaInSquareMeters',  width: width(105),  style: fixed   },
      { header: { key: 'unit.code',     replaceMap, modifier }, key: 'unitCodes',           width: width(155),  style          },
      { header: { key: 'label.bankAccount'                   }, key: 'bankAccountNumber',   width: width(300),  style          }
    ];
  }
}

export namespace PRSReport {
  export interface AgreementData {
    id: string;
    agreementNumber: string;
    bankAccount: string;
    startDate: string;
    endDate: string;
    formerEndDate: string;
    status: string;
    protocolDate: string;
    rent: number;
    deposit: number;
    paymentDay: number;
    currency: string;
    serviceCharge: number;
    recipientNumber: string;
    units: string;
  }

  export interface ResidentData extends ResidentReportData {
    type: string;
    ownerCode: string;
    agreementNumber: string;
    NIP: string;
    REGON: string;
  }

  export interface RentalUnitData {
    id: string;
    agreementNumber: string;
    rentStartDate: string;
    rentEndDate: string;
    type: string;
    street: string;
    blockNumber: string;
    staircaseNumber: string;
    floor: string;
    number: string;
    area: number;
    unitCodes: string;
  }

  export interface AgreementsNote extends GenericNoteExportData {
    name: string | null;
  }
}

export class PRSReportColumns {
  // prettier-ignore
  public static agreement(reportType: string): ExcelFile.Column[] {
    const withArchived =
      reportType == 'all' || reportType == 'archived'
        ? [{ header: { key: 'agr.endArchived' }, key: 'formerEndDate', width: width(180), style }]
        : [];
    return [
      { header: { key: 'table.id'                    }, key: 'id',              width: width(45),   style },
      { header: { key: 'agr.recipientNo'             }, key: 'recipientNumber', width: width(130),  style },
      { header: { key: 'agr.number',        modifier }, key: 'agreementNumber', width: width(155),  style },
      { header: { key: 'agr.start'                   }, key: 'startDate',       width: width(130),  style },
      { header: { key: 'agr.end'                     }, key: 'endDate',         width: width(180),  style },
      ...withArchived,
      { header: { key: 'agr.status',        modifier }, key: 'status',          width: width(130),  style },
      { header: { key: 'agr.protDate'                }, key: 'protocolDate',    width: width(130),  style },
      { header: { key: 'agr.rent',          modifier }, key: 'rent',            width: width(70),   style },
      { header: { key: 'agr.deposit'                 }, key: 'deposit',         width: width(100),  style },
      { header: { key: 'agr.serviceCharge'           }, key: 'serviceCharge',   width: width(120),  style },
      { header: { key: 'label.currency',    modifier }, key: 'currency',        width: width(65),   style },
      { header: { key: 'invoice.dueDate'             }, key: 'paymentDay',      width: width(85),   style },
      { header: { key: 'label.bankAccount'           }, key: 'bankAccount',     width: width(300),  style },
      { header: { key: 'navigation.units'            }, key: 'units',           width: width(300),  style }
    ];
  }

  // prettier-ignore
  public static readonly landlordTenant: ExcelFile.Column[] = [
    { header: { key: 'table.id'                    }, key: 'id',                width: width(45),   style },
    { header: { key: 'agr.number',        modifier }, key: 'agreementNumber',   width: width(155),  style },
    { header: { key: 'label.type',        modifier }, key: 'type',              width: width(85),   style },
    ...PERSONAL_DATA_COLUMNS,
    { header: { key: 'label.nip'                   }, key: 'NIP',               width: width(100),  style },
    { header: { key: 'label.regon'                 }, key: 'REGON',             width: width(100),  style },
    { header: { key: 'user.ownerCode'              }, key: 'ownerCode',         width: width(255),  style },
    { header: { key: 'user.emergeContact'          }, key: 'emergencyContact',  width: width(255),  style },
    { header: { key: 'label.lastUpdate'            }, key: 'lastUpdate',        width: width(200),  style }
  ];

  // prettier-ignore
  public static readonly rentalUnits: ExcelFile.Column[] = [
    { header: { key: 'table.id'                }, key: 'id',                  width: width(45),   style        },
    { header: { key: 'agr.number',    modifier }, key: 'agreementNumber',     width: width(155),  style        },
    { header: { key: 'label.type',    modifier }, key: 'type',                width: width(85),   style        },
    { header: { key: 'agr.startDate'           }, key: 'rentStartDate',       width: width(130),  style        },
    { header: { key: 'agr.endDate'             }, key: 'rentEndDate',         width: width(130),  style        },
    ...RENTAL_UNIT_COLUMNS(),
    { header: { key: 'unit.area'               }, key: 'areaInSquareMeters',  width: width(105),  style: fixed },
    { header: { key: 'label.mpk'               }, key: 'unitCodes',           width: width(155),  style        }
  ];

  public static readonly agreementsNotes: ExcelFile.Column[] = [
    { header: { key: 'agr.number', modifier }, key: 'name', width: width(155), style },
    ...genericNoteTableColumns
  ];
}
